import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Modal } from '@hdi-seguros/web-components.modal';
import { Form as FormAnt, ModalProps, message } from "antd";

import { Endorsement, InsurancePolicy, Quote, SelectOption, UserInformation } from "../../../../../shared/interfaces";

import { PlusOutlined } from "@ant-design/icons";
import { ActionButton } from "@hdi-seguros/web-components.action-button";
import dayjs from "dayjs";
import { ColSize, DatePickerForm, Form, InputCurrencyForm, RowSize, SelectForm, UploadFileModal } from "../../../../../shared/components";
import { AuthContext, UIContext } from "../../../../../shared/contexts";
import { InformFeePaidRequest } from "../../../../../shared/interfaces/services/inform-fee-paid-request";
import { DocumentUploadServiceRequestParams, DocumentUploadServiceResponse } from "../../../../../shared/interfaces/services/post-send-claim-documents-service";
import { UploadedFile } from "../../../../../shared/interfaces/uploaded-file.interface";
import PreLiquidationService from "../../../../../shared/services/collection/preliquidation";
import DocumentsService from "../../../../../shared/services/documents";
import InsurancePolicyService from "../../../../../shared/services/insurancePolicy";
import { getUserInfo, ValidateForm } from "../../../../../shared/utils";
import { AlertComponent } from "../../../../../shared/components/AlertComponent";

import './index.css';
import { DocumentClass } from "../../../../../shared/interfaces/enums/document-class-enum";

interface ReportFeePaidModalProps extends ModalProps {
    insurancePolicyDetail: InsurancePolicy;
    endorsement: Endorsement;
    quote: Quote;
    onClose: () => void;
    onUpdate: () => void;
}

export const ReportFeePaidModal: FC<ReportFeePaidModalProps> = ({ open, title, onClose, onUpdate, quote, endorsement, insurancePolicyDetail }) => {

    const { id } = useParams();
    const authContext = useContext(AuthContext);
    const user = authContext.userInfo as UserInformation;
    const [form] = FormAnt.useForm();
    const [loading, setLoading] = useState(false);
    const validateForm = new ValidateForm();
    const { setIsLoading, setShowError } = useContext(UIContext);
    const [bankList, setBankList] = useState<SelectOption[]>([]);
    const [canShowUploadModalUploadFile, setCanShowUploadModalUploadFile] = useState<boolean>(false);
    const [fileSelect, setFileSelect] = useState<UploadedFile[]>([]);


    const handleAccept = async () => {
        try {
            await form.validateFields();
            if(fileSelect.length > 0) {
                await uploadFiles(fileSelect);
            }   else {
                message.error('No se ha seleccionado ningún archivo.');
                return;
            }
            onUpdate();
        } catch (error) {

        }
    };

    const formatLabelWithSpace = (str: string) =>
        str.replace(/\(/g, ' (');

    const getBanksOptions = useCallback(async () => {
        try {

            const response = await PreLiquidationService.getBanksList();

            if (response) {
                const transformedData = response.data.bancosPreliquidacion.map(
                    (item) => ({
                        label: formatLabelWithSpace(item.descripcion),
                        value: item.codigo.toString(),
                    })
                );

                setBankList(transformedData);
            }

        } catch (error) {
        }
    }, [
        setBankList,
    ]);

    const getComboInfo = useCallback(() => {
        Promise.all([getBanksOptions()])
            .then(() => {

            })
            .catch(error => { setShowError(true) });
    }, [getBanksOptions]);

    useEffect(() => {
        getComboInfo();
    }, []);

    function getPkNameFilenet() {
        const padLeft = (value: string | number | null | undefined, length: number): string => {
            return String(value ?? '').padStart(length, '0');
        };

        const article = padLeft(insurancePolicyDetail.articulo, 6);
        const superPolicy = padLeft(insurancePolicyDetail.superpoliza, 9);
        const supplementPolicy = padLeft(quote.suplemento, 3);
        const branch = padLeft(insurancePolicyDetail.rama, 2);
        const policyNumber = padLeft(insurancePolicyDetail.nroPoliza, 7);
        const supplementSuperPolicy = padLeft(quote.suplemento, 3);
        const quoteNumber = padLeft(quote.nroCuota, 2);

        const now = new Date();
        const formatedDate = now.toISOString().slice(0, 10).replace(/-/g, '') + '-' +
            now.toTimeString().slice(0, 8).replace(/:/g, '');

        const fileNamePK = `${article}_${superPolicy}_${supplementSuperPolicy}_${branch}_${policyNumber}_${supplementPolicy}_${quoteNumber}_${formatedDate}`;
        return fileNamePK;
    }

    function getFileExtension(name: string): string | null {
        const parts = name.split('.');
        if (parts.length > 1) {
            return "." + parts.pop()?.toLowerCase() || null;
        }
        return null;
    }

    const uploadFiles = async (fileSelect: UploadedFile[]) => {
        try {
            let response: DocumentUploadServiceResponse | undefined;
            let fileName = "";
            await Promise.all(
                fileSelect.map(async (file) => {
                    try {
                        fileName = `RECIBOPAGOTRANSFERENCIA_${getPkNameFilenet()}${getFileExtension(file.name)}`;
                        const requestParams: DocumentUploadServiceRequestParams = {
                            uploadDocument: {
                                documentClass: DocumentClass.PaymentReceiptTransfer,
                                fileName: fileName,
                                fileType: file.mediaType,
                                base64: file.base.split(",")[1],
                                applicationId: "WebProductores2.0",
                                author: user.usuarioInterno,
                                attributes: [
                                    { id: "cmis:objectTypeId", type: "String", value: DocumentClass.PaymentReceiptTransfer },
                                    { id: "DocumentTitle", type: "String", value: fileName },
                                    { id: "cmis:name", type: "String", value: fileName },
                                    { id: "Superpoliza", type: "String", value: insurancePolicyDetail.superpoliza },
                                    { id: "SuplementoSuperpoliza", type: "String", value: quote.suplemento},
                                    { id: "SuplementoPoliza", type: "String", value: quote.suplemento },
                                    { id: "Poliza", type: "String", value: insurancePolicyDetail.nroPoliza },
                                    { id: "Articulo", type: "String", value: insurancePolicyDetail.articulo},
                                    { id: "Rama", type: "String", value: insurancePolicyDetail.rama },
                                    { id: "Cuota", type: "String", value: quote.nroCuota },
                                ],
                            },
                        };
                        const axiosResponse = await DocumentsService.sendReportFeePaidDocument(requestParams);
                        response = axiosResponse.data;
                    } catch (error) {
                    }
                })
            );

            if (response) {
                try {                    
                    const transferDate = dayjs(form.getFieldValue('transferDate')).format('YYYY-MM-DD');
                    const base = getUserInfo();
                    const InformFeePaidRequest: InformFeePaidRequest = {
                        ...base,
                        articulo: Number(insurancePolicyDetail.articulo),
                        superpoliza: Number(insurancePolicyDetail.superpoliza),
                        codigoBanco: Number(form.getFieldValue('bank')),
                        fechaTransferencia: transferDate,
                        importe: Number(form.getFieldValue('exactAmount')),
                        numeroCuota: Number(quote.nroCuota),
                        poliza: Number(insurancePolicyDetail.nroPoliza),
                        archivo: fileName,
                        rama: Number(insurancePolicyDetail.rama),
                        suplementoPoliza: Number(quote.suplemento),
                        suplementoSuperpoliza: Number(quote.suplemento),
                    };
                    const responseInform = await InsurancePolicyService.informReportFeePaidDocument(InformFeePaidRequest);
                    if (responseInform) {
                        message.success('Cuota pagada informada con éxito!');
                        onClose();
                        onUpdate();
                    }
                } catch (error) {

                }
            }
        } catch (error) {
        }
    };

    return (
        <Modal
            title={title}
            centered
            open={open}
            width={650}
            onOk={handleAccept}
            onCancel={onClose}
            confirmText={'Aceptar'}
            footer
            loadingConfirmButton={loading}
            className="cabcel-policy-modal-container"
        >
            <>

                <Form
                    formProps={{
                        form,
                        name: "cancellation-digitial-policy-form",

                        style: { marginBottom: 10, marginTop: 10 },
                    }}
                >
                    <RowSize rowProps={{ gutter: [15, 15], justify: 'start' }} className="row-insurance-cancel-policy" >
                        <ColSize size={25}>
                            <DatePickerForm
                                formProps={{
                                    label: "Fecha de transferencia",
                                    required: true,
                                    name: "transferDate",
                                    rules: validateForm.generateRules({
                                        label: "fecha de transferencia",
                                        rules: ["required"],
                                    }),
                                }}
                                datePickerProps={{
                                    maxDate: dayjs(),
                                    placeholder: dayjs().format("DD/MM/YYYY"),
                                }}
                            />
                        </ColSize>
                        <ColSize size={25} >
                            <SelectForm
                                formProps={{
                                    label: "Banco:",
                                    name: "bank",
                                    rules: validateForm.generateRules({
                                        label: "Banco",
                                        rules: ["required"],
                                    }),
                                }}
                                selectProps={{
                                    value: "code",
                                    loading: bankList?.length === 0,
                                    disabled: bankList?.length === 0,
                                    options: bankList,
                                    showSearch: true,
                                }}
                            />
                        </ColSize>
                        <ColSize size={25} >
                            <InputCurrencyForm
                                formProps={{
                                    label: 'Importe de la transferencia',
                                    name: 'exactAmount',
                                    rules: validateForm.generateRules({
                                        label: 'Importe Exacto',
                                        rules: ['required'],
                                    }),
                                }}
                                inputCurrencyProps={{
                                    precision: 2,
                                    step: 0.01,
                                    stringMode: true,
                                    placeholder: "Ingrese un monto",
                                    onKeyDown: (e) => {
                                        const input = e.currentTarget;
                                        const value = input.value;

                                        const isComma = e.key === ",";
                                        const isMinus = e.key === "-";
                                        const isDot = e.key === ".";

                                        if (isComma && value.includes(",")) {
                                            e.preventDefault();
                                        }

                                        if (
                                            isMinus &&
                                            (value.includes("-") || input.selectionStart !== 0)
                                        ) {
                                            e.preventDefault();
                                        }
                                        if (isDot) {
                                            e.preventDefault();
                                            if(!value.includes(",")){
                                                const start = input.selectionStart ?? 0;
                                                const end = input.selectionEnd ?? 0;
                                            
                                                const newValue = value.slice(0, start) + "," + value.slice(end);
                                                input.value = newValue;
                                            
                                                input.setSelectionRange(start + 1, start + 1);
                                            
                                                const event = new Event("input", { bubbles: true });
                                                input.dispatchEvent(event);
                                            }
                                            return;
                                        }

                                        if (!/[0-9,-]/.test(e.key) && e.key.length === 1) {
                                            e.preventDefault();
                                        }
                                    },
                                    formatter: (value: string | number | undefined) => {
                                        if (!value) return "";
                                        const number = Number(value.toString().replace(",", "."));
                                        return new Intl.NumberFormat("es-AR", {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 2,
                                        }).format(number);
                                    },
                                    parser: (value: string | undefined) => {
                                        if (!value) return "";

                                        const cleaned = value.replace(/\./g, "");

                                        if ((cleaned.match(/,/g) || []).length > 1) return "";

                                        return cleaned.replace(",", ".");
                                    },
                                }}
                            />
                        </ColSize>
                        <ColSize size={100}>
                            <AlertComponent message="Debe ingresar el importe exacto de la transferencia" type="warning" />
                        </ColSize>
                        <ColSize size={25} >
                            <ActionButton
                                type="secondary"
                                className='btn-accent-outline-barbuss'
                                label='Agregar Comprobante'
                                onClick={() => setCanShowUploadModalUploadFile(true)}
                                icon={<PlusOutlined />}
                            />
                            {fileSelect.map((file, index) => (
                                <p key={file.uid ?? index} >{file.name}</p>
                            ))}
                            <UploadFileModal
                                canShowUploadModalUploadFile={canShowUploadModalUploadFile}
                                formatFile={'PDF, JPEG, JPG o PNG'}
                                canSelectDocumentType={false}
                                setCanShowUploadModalUploadFile={setCanShowUploadModalUploadFile}
                                setFileSelect={setFileSelect}
                                isRV={false}
                            />
                        </ColSize>


                    </RowSize>
                </Form>
            </>
        </Modal>
    )
}
